import { useCallback, useEffect, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  signInAnonymously,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  getDocFromServer,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

import app, { auth, db } from '../firebase/firebaseConfig';
import {
  getGuestBootstrapPromise,
  prefetchGuestAuth,
} from '../firebase/guestAuthPrefetch';
import { clearGuestStudyState } from '../utils/demoState';

/**
 * Guest session bootstrap.
 *
 * Fast path: signInAnonymously immediately so the app shell can render, then
 * reconcile device_map + fingerprint in the background.
 */

const DEVICE_MAP_COLLECTION = 'device_map';
const LS_UID_KEY = 'fc:anonUid';
const LS_FP_KEY = 'fc:deviceFp';
const FUNCTIONS_REGION = 'us-central1';

const DEVICE_MAP_CONFLICT_CODE = 'device-map-conflict';
const GUEST_ACCOUNT_REGISTERED_CODE = 'guest-account-registered';

const isLocalGuestDevBypass = () =>
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_GUEST_DEV_BYPASS !== 'false' &&
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

const isAuthRoute = () =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/auth');

let fingerprintAgentPromise = null;
const loadFingerprintAgent = () => {
  if (!fingerprintAgentPromise) {
    fingerprintAgentPromise = FingerprintJS.load({ monitoring: false });
  }
  return fingerprintAgentPromise;
};

if (typeof window !== 'undefined' && !isLocalGuestDevBypass()) {
  loadFingerprintAgent().catch(() => {});
}

let bootstrapInFlight = null;
let reconcileInFlight = null;

const safeLocalStorage = {
  get(key) {
    try {
      return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === 'undefined') return;
      if (value == null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
};

const getRequestUserAgent = () =>
  typeof navigator !== 'undefined'
    ? String(navigator.userAgent || '').slice(0, 256)
    : null;

const createConflictError = (mappedUid) => {
  const err = new Error('Device mapping conflict');
  err.code = DEVICE_MAP_CONFLICT_CODE;
  err.mappedUid = mappedUid;
  return err;
};

const createRegisteredGuestError = () => {
  const err = new Error('Guest progress belongs to a registered account');
  err.code = GUEST_ACCOUNT_REGISTERED_CODE;
  return err;
};

const isGuestCallableDenied = (err) => err?.code === 'functions/permission-denied';

const isStoredUserAgentMismatch = (mapData) => {
  const stored =
    mapData && typeof mapData.userAgent === 'string' ? mapData.userAgent : null;
  const current = getRequestUserAgent();
  return Boolean(stored && current && stored !== current);
};

const getDeviceFingerprint = async () => {
  const cached = safeLocalStorage.get(LS_FP_KEY);
  if (cached) {
    loadFingerprintAgent()
      .then((agent) => agent.get())
      .then((result) => {
        if (result.visitorId && result.visitorId !== cached) {
          safeLocalStorage.set(LS_FP_KEY, result.visitorId);
        }
      })
      .catch(() => {});
    return cached;
  }

  const agent = await loadFingerprintAgent();
  const result = await agent.get();
  return result.visitorId;
};

/** Brief poll while Firebase persistence rehydrates an anonymous session. */
export async function waitForPersistedAnonymousUser(maxMs = 50) {
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    await auth.authStateReady();
    if (auth.currentUser?.isAnonymous) {
      return auth.currentUser;
    }
    await new Promise((resolve) => setTimeout(resolve, 25));
  }
  return null;
}

/**
 * Kick off anonymous auth as early as possible (module load / first paint).
 * Safe to await from AppRoutes — deduped via bootstrapInFlight.
 */
export function prefetchGuestSession() {
  if (typeof window === 'undefined' || isAuthRoute() || isLocalGuestDevBypass()) {
    return null;
  }
  return prefetchGuestAuth() || getGuestBootstrapPromise();
}

/**
 * Minimum work needed before Firestore rules accept reads: anonymous Firebase user.
 */
export async function bootstrapGuestSessionFast() {
  const existing = getGuestBootstrapPromise();
  if (existing) {
    try {
      return await existing;
    } catch {
      /* fall through to retry */
    }
  }
  if (bootstrapInFlight) return bootstrapInFlight;

  bootstrapInFlight = (async () => {
    await auth.authStateReady();
    if (auth.currentUser) {
      return auth.currentUser;
    }
    const started = prefetchGuestAuth();
    if (started) {
      return started;
    }
    const cred = await signInAnonymously(auth);
    safeLocalStorage.set(LS_UID_KEY, cred.user.uid);
    return cred.user;
  })();

  try {
    return await bootstrapInFlight;
  } finally {
    bootstrapInFlight = null;
  }
}

const readDeviceMapSnap = async (mapRef) => {
  const cachedSnap = await getDoc(mapRef);
  if (!cachedSnap.exists()) {
    return cachedSnap;
  }

  try {
    return await getDocFromServer(mapRef);
  } catch (serverErr) {
    console.warn('[useAnonymousAuth] device_map server read failed, using cache:', serverErr);
    return cachedSnap;
  }
};

const restoreMappedGuestSession = async (fingerprint, mappedUid) => {
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const restoreGuestSession = httpsCallable(functions, 'restoreGuestSession');
  const result = await restoreGuestSession({
    fingerprint,
    userAgent: getRequestUserAgent(),
  });
  const token = result?.data?.token;
  if (!token) {
    throw new Error('restore-failed-no-token');
  }

  const cred = await signInWithCustomToken(auth, token);
  if (cred.user.uid !== mappedUid) {
    try {
      await signOut(auth);
    } catch {
      /* ignore */
    }
    throw new Error('restore-uid-mismatch');
  }
  return cred.user;
};

const resetGuestSessionViaCallable = async (fingerprint) => {
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const resetGuestSession = httpsCallable(functions, 'resetGuestSession');
  const result = await resetGuestSession({ fingerprint });
  const data = result?.data || {};
  if (data.reason === 'registered') {
    throw createRegisteredGuestError();
  }
  return data;
};

const claimDeviceMap = async (mapRef, signedInUser) => {
  await runTransaction(db, async (tx) => {
    const fresh = await tx.get(mapRef);
    if (fresh.exists()) {
      const otherUid = fresh.data()?.uid;
      if (otherUid && otherUid !== signedInUser.uid) {
        throw createConflictError(otherUid);
      }
      return;
    }
    tx.set(mapRef, {
      uid: signedInUser.uid,
      createdAt: serverTimestamp(),
      userAgent: getRequestUserAgent(),
    });
  });
};

const remapCurrentGuestSession = async (deviceFp, user) => {
  const mapRef = doc(db, DEVICE_MAP_COLLECTION, deviceFp);
  try {
    await resetGuestSessionViaCallable(deviceFp);
  } catch (resetErr) {
    if (resetErr?.code === GUEST_ACCOUNT_REGISTERED_CODE) {
      throw resetErr;
    }
    console.warn('[useAnonymousAuth] reset during remap failed:', resetErr);
  }
  try {
    await claimDeviceMap(mapRef, user);
    safeLocalStorage.set(LS_UID_KEY, user.uid);
  } catch (claimErr) {
    console.warn('[useAnonymousAuth] remap claim failed:', claimErr);
  }
  return user;
};

const startFreshGuestSession = async (deviceFp) => {
  clearGuestStudyState();
  safeLocalStorage.set(LS_UID_KEY, null);

  if (auth.currentUser?.isAnonymous) {
    try {
      await signOut(auth);
    } catch {
      /* ignore */
    }
  }

  const mapRef = doc(db, DEVICE_MAP_COLLECTION, deviceFp);
  const resetPromise = resetGuestSessionViaCallable(deviceFp).catch((resetErr) => {
    if (resetErr?.code === GUEST_ACCOUNT_REGISTERED_CODE) {
      throw resetErr;
    }
    console.warn('[useAnonymousAuth] resetGuestSession failed:', resetErr);
    return null;
  });
  const signInPromise = signInAnonymously(auth);

  await resetPromise;
  const cred = await signInPromise;
  const signedInUser = cred.user;

  try {
    await claimDeviceMap(mapRef, signedInUser);
  } catch (txErr) {
    if (txErr?.code !== DEVICE_MAP_CONFLICT_CODE) {
      throw txErr;
    }
    await remapCurrentGuestSession(deviceFp, signedInUser);
  }

  safeLocalStorage.set(LS_UID_KEY, signedInUser.uid);
  return signedInUser;
};

/**
 * Background reconciliation — keeps the current anonymous user when possible.
 */
export async function reconcileGuestDeviceMap() {
  if (isLocalGuestDevBypass()) {
    return auth.currentUser;
  }
  if (reconcileInFlight) {
    return reconcileInFlight;
  }

  reconcileInFlight = (async () => {
    await auth.authStateReady();
    const user = auth.currentUser;
    if (!user?.isAnonymous) {
      return user;
    }

    let deviceFp;
    try {
      deviceFp = await getDeviceFingerprint();
      safeLocalStorage.set(LS_FP_KEY, deviceFp);
    } catch (fpErr) {
      console.warn('[useAnonymousAuth] fingerprint failed during reconcile:', fpErr);
      return user;
    }

    const mapRef = doc(db, DEVICE_MAP_COLLECTION, deviceFp);
    let snap;
    try {
      snap = await readDeviceMapSnap(mapRef);
    } catch (readErr) {
      console.warn('[useAnonymousAuth] device_map read failed during reconcile:', readErr);
      return user;
    }

    if (!snap.exists()) {
      try {
        await claimDeviceMap(mapRef, user);
        safeLocalStorage.set(LS_UID_KEY, user.uid);
      } catch (claimErr) {
        console.warn('[useAnonymousAuth] device_map claim failed:', claimErr);
      }
      return user;
    }

    const mapData = snap.data();
    const mappedUid = mapData?.uid || null;
    if (mappedUid === user.uid) {
      safeLocalStorage.set(LS_UID_KEY, user.uid);
      return user;
    }

    if (isStoredUserAgentMismatch(mapData)) {
      return remapCurrentGuestSession(deviceFp, user);
    }

    try {
      const restoredUser = await restoreMappedGuestSession(deviceFp, mappedUid);
      safeLocalStorage.set(LS_UID_KEY, mappedUid);
      return restoredUser;
    } catch (restoreErr) {
      console.warn('[useAnonymousAuth] background restore failed:', restoreErr);
      if (isGuestCallableDenied(restoreErr)) {
        return remapCurrentGuestSession(deviceFp, user);
      }
      return user;
    }
  })();

  try {
    return await reconcileInFlight;
  } finally {
    reconcileInFlight = null;
  }
}

const waitForRestoredAnonSession = async (mappedUid, { attempts = 3, delayMs = 50 } = {}) => {
  for (let i = 0; i < attempts; i += 1) {
    await auth.authStateReady();
    const current = auth.currentUser;
    if (current?.isAnonymous && current.uid === mappedUid) {
      return current;
    }
    if (i < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
};

const ensureAnonymousSessionFull = async () => {
  await auth.authStateReady();

  if (auth.currentUser && !auth.currentUser.isAnonymous) {
    return auth.currentUser;
  }

  if (auth.currentUser?.isAnonymous) {
    return reconcileGuestDeviceMap();
  }

  let deviceFp;
  try {
    deviceFp = await getDeviceFingerprint();
    safeLocalStorage.set(LS_FP_KEY, deviceFp);
  } catch (fpErr) {
    console.warn('[useAnonymousAuth] FingerprintJS failed:', fpErr);
    return bootstrapGuestSessionFast();
  }

  const mapRef = doc(db, DEVICE_MAP_COLLECTION, deviceFp);
  let mappedUid = null;
  let mapData = null;

  try {
    const snap = await readDeviceMapSnap(mapRef);
    if (snap.exists()) {
      mapData = snap.data();
      mappedUid = mapData?.uid || null;
    }
  } catch (readErr) {
    console.warn('[useAnonymousAuth] device_map read failed:', readErr);
    return bootstrapGuestSessionFast();
  }

  if (mappedUid) {
    if (isStoredUserAgentMismatch(mapData)) {
      const user = await bootstrapGuestSessionFast();
      return remapCurrentGuestSession(deviceFp, user);
    }

    const persisted = await waitForRestoredAnonSession(mappedUid);
    if (persisted) {
      safeLocalStorage.set(LS_UID_KEY, mappedUid);
      return persisted;
    }

    try {
      const restoredUser = await restoreMappedGuestSession(deviceFp, mappedUid);
      safeLocalStorage.set(LS_UID_KEY, mappedUid);
      return restoredUser;
    } catch (restoreErr) {
      console.warn('[useAnonymousAuth] restore failed, starting fresh:', restoreErr);
      return startFreshGuestSession(deviceFp);
    }
  }

  const user = await bootstrapGuestSessionFast();
  try {
    await claimDeviceMap(mapRef, user);
  } catch (txErr) {
    if (txErr?.code === DEVICE_MAP_CONFLICT_CODE) {
      return startFreshGuestSession(deviceFp);
    }
    console.warn('[useAnonymousAuth] device_map write failed:', txErr);
  }
  safeLocalStorage.set(LS_UID_KEY, user.uid);
  return user;
};

export default function useAnonymousAuth(options = {}) {
  const { enabled = true, autoStart = true, strictOnError = false } = options;

  const [user, setUser] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fingerprint, setFingerprint] = useState(
    () => safeLocalStorage.get(LS_FP_KEY)
  );

  const inFlightRef = useRef(null);

  const ensureAnonymousSession = useCallback(async () => {
    if (!enabled) return auth.currentUser;
    if (inFlightRef.current) return inFlightRef.current;

    const run = (async () => {
      setError(null);
      try {
        const sessionUser = await ensureAnonymousSessionFull();
        setUser(sessionUser);
        return sessionUser;
      } catch (e) {
        if (strictOnError) {
          setError(e);
          throw e;
        }
        console.warn('[useAnonymousAuth] ensureAnonymousSession failed:', e);
        const fallback = await bootstrapGuestSessionFast();
        setUser(fallback);
        return fallback;
      } finally {
        setLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = run;
    return run;
  }, [enabled, strictOnError]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u && u.isAnonymous) {
        safeLocalStorage.set(LS_UID_KEY, u.uid);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!enabled || !autoStart) return undefined;
    let cancelled = false;
    ensureAnonymousSession().catch((e) => {
      if (cancelled) return;
      console.warn('[useAnonymousAuth] auto-start failed:', e);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, autoStart, ensureAnonymousSession]);

  return {
    user,
    loading,
    error,
    fingerprint,
    ensureAnonymousSession,
  };
}

export { GUEST_ACCOUNT_REGISTERED_CODE, DEVICE_MAP_CONFLICT_CODE };
